import {
    AccordionDetails,
    AccordionSummary,
    Box,
    Divider,
    Grid2,
    Link,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { ExpandMore, Facebook, Instagram, LinkedIn, X } from '@mui/icons-material';
import React, { useState } from 'react';
import { StyledSocialIcon, StyledVesisIcon } from 'src/styledComponents/styledIcons';

import { Routes } from 'src/utils/enums';
import { StyledBoxAccordion } from 'src/styledComponents/styledAccordions';
import { content } from 'src/common/lang/en-US';
import footerBgImg from 'src/resources/images/Funky_Lines.png';

const FooterPage: React.FC = () => {
    const theme = useTheme();
    const isMediumScreens = useMediaQuery(theme.breakpoints.down('md'));

    const { headings, subHeadings, waterMarkContent } = content.footer;
    const [expandedPanel, setExpandedPanel] = useState<number | false>(false);

    const handleChange = (panel: number) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpandedPanel(isExpanded ? panel : false);
    };
    const sections = [
        {
            heading: headings.company,
            links: [
                { link: Routes.ABOUT, name: subHeadings.aboutUs },
                { link: Routes.COMING_SOON, name: subHeadings.careers },
                { link: Routes.COMING_SOON, name: subHeadings.press },
                { link: Routes.COMING_SOON, name: subHeadings.blog },
            ],
        },
        {
            heading: headings.products,
            links: [
                { link: Routes.COMING_SOON, name: subHeadings.features },
                { link: Routes.COMING_SOON, name: subHeadings.pricing },
                { link: Routes.COMING_SOON, name: subHeadings.demo },
                { link: Routes.COMING_SOON, name: subHeadings.integrations },
            ],
        },
        {
            heading: headings.support,
            links: [
                { link: Routes.CONTACT_US, name: subHeadings.contactUs },
                { link: Routes.COMING_SOON, name: subHeadings.help },
                { link: Routes.COMING_SOON, name: subHeadings.community },
            ],
        },
        {
            heading: headings.legal,
            links: [
                { link: Routes.PRIVACY_POLICY, name: subHeadings.privacy },
                { link: Routes.COMING_SOON, name: subHeadings.terms },
                { link: Routes.COMING_SOON, name: subHeadings.compliance },
                { link: Routes.COMING_SOON, name: subHeadings.security },
            ],
        },
    ];

    const socialLinks = [
        {
            icon: Facebook,
            id: 'facebook',
            link: 'https://www.facebook.com/profile.php?id=61572578986638',
        },
        { icon: Instagram, id: 'instagram', link: 'https://www.instagram.com/vesis.inc/' },
        { icon: X, id: 'twitter', link: 'https://in.linkedin.com/company/vesis' },
        { icon: LinkedIn, id: 'linkedIn', link: 'https://x.com/VesisInc' },
    ];

    const getVesisBrandingSection = (): React.ReactNode => {
        return (
            <Grid2
                display="flex"
                flexDirection="column"
                gap="2rem"
                justifyContent="space-between"
                size={{ lg: 1, md: 1, sm: 6 }}
            >
                <StyledVesisIcon />
                <Box display="flex" gap={{ xs: 2, xxl: 4 }}>
                    {socialLinks.map((socialLink) => (
                        <Link href={socialLink.link} key={socialLink.id} target="_blank">
                            <StyledSocialIcon as={socialLink.icon} sx={{ color: 'grey.500' }}></StyledSocialIcon>
                        </Link>
                    ))}
                </Box>
            </Grid2>
        );
    };

    const getMobileView = (): React.ReactNode => {
        return (
            <Grid2 columns={6} container gap="2rem">
                {getVesisBrandingSection()}
                <Grid2 size={6}>
                    <Stack gap="0.25rem">
                        {sections?.map((section, index) => (
                            <StyledBoxAccordion
                                expanded={expandedPanel === index}
                                key={`${section.heading}-${index}`}
                                onChange={handleChange(index)}
                            >
                                <AccordionSummary expandIcon={<ExpandMore sx={{ color: 'grey.500' }} />}>
                                    <Typography color="other.darkBlue" variant="h7">
                                        {section.heading}
                                    </Typography>
                                </AccordionSummary>
                                <Divider sx={{ borderColor: 'grey.100' }} />
                                <AccordionDetails>
                                    <Stack gap="0.5rem" padding="0.5rem">
                                        {section.links.map((ele, index) => (
                                            <Link href={ele.link} key={`${ele.name}:${index}`} underline="none">
                                                <Typography color="other.lightBlack" variant="caption1_Thin">
                                                    {ele.name}
                                                </Typography>
                                            </Link>
                                        ))}
                                    </Stack>
                                </AccordionDetails>
                            </StyledBoxAccordion>
                        ))}
                    </Stack>
                </Grid2>
                <Grid2 justifyContent="center" paddingTop="1rem" size={11} textAlign="center">
                    <Typography color="grey.500" variant="finePrint">
                        {waterMarkContent.content}
                    </Typography>
                </Grid2>
            </Grid2>
        );
    };

    return (
        <Box
            pb={'2rem'}
            pt={'4rem'}
            px={{ md: '3rem', xs: '2rem' }}
            sx={{
                backgroundColor: 'lightgray', // Fallback background color
                backgroundImage: `linear-gradient(90deg, #E9F4F6 0%, rgba(233, 244, 246, 0.40) 99.86%), url(${footerBgImg})`,
                backgroundPosition: '0% 0%', // Starting position of the background image
                backgroundRepeat: 'repeat', // Repeating pattern for the image
            }}
        >
            {isMediumScreens ? (
                getMobileView()
            ) : (
                <Grid2 columns={6} container justifyContent="space-between" rowGap="2rem">
                    {getVesisBrandingSection()}
                    {sections.map((ele, index) => (
                        <Grid2 justifyContent="space-around" key={`${ele.heading}:${index}`} p={1} size={1}>
                            <Stack>
                                <Typography color="other.darkBlue" mb={1} variant="h5">
                                    {ele.heading}
                                </Typography>
                                {ele.links.map((link, index) => (
                                    <Link href={link.link} key={`${link.name}:${index}`} underline="none">
                                        <Typography color="other.lightBlack" variant="caption1_Thin">
                                            {link.name}
                                        </Typography>
                                    </Link>
                                ))}
                            </Stack>
                        </Grid2>
                    ))}
                    <Grid2 paddingTop="1rem" size={6} textAlign="center">
                        <Typography color="grey.500" variant="finePrint">
                            {waterMarkContent.content}
                        </Typography>
                    </Grid2>
                </Grid2>
            )}
        </Box>
    );
};

export default FooterPage;
